import { compact, findFirst } from 'fp-ts/lib/Array';
import { fromNullable, fromPredicate, none, some } from 'fp-ts/lib/Option';
import { getAlias, getCSRF, getLang } from 'sdi/app';
import { nameToString } from 'sdi/components/button/names';
import {
    DETAILS,
    DIV,
    FORM,
    INPUT,
    SPAN,
    SUMMARY,
    TABLE,
    TBODY,
    TH,
    THEAD,
    TR,
} from 'sdi/components/elements';
import { ExportForm } from 'sdi/components/export';
import renderConfig from 'sdi/components/feature-view';
import tr, {
    concat,
    formatDate,
    formatNumber,
    fromRecord,
    MessageKey,
} from 'sdi/locale';
import {
    DirectGeometryObject,
    Feature,
    FeatureCollection,
    featureViewDefault,
    FieldsDescriptor,
    getFields,
    getMessageRecord,
    withoutWidget,
} from 'sdi/source';
import {
    checkURLScheme,
    Collection,
    ensureArray,
    getLayerPropertiesKeys,
    tryDate,
    tryNumber,
} from 'sdi/util';
import {
    getDatasetMetadataOption,
    getMapLayers,
    getMapOption,
} from 'view/src/queries/app';
import { getGeometry, getHarvests } from 'view/src/queries/harvest';
import { HarvestedLayer, HarvestedLayerLoaded } from './index';

export const getRessourceTitle = (metadataId: string) =>
    getDatasetMetadataOption(metadataId).fold(metadataId, md =>
        fromRecord(getMessageRecord(md.resourceTitle)),
    );

const renderTitle = (mid: string) =>
    DIV({ className: 'layer-title' }, getRessourceTitle(mid));

const roundCoords = (coords: number[]) => coords.map(c => Math.round(c));

const roundPolygonCoords = (coords: number[][][]) =>
    coords.map(list => list.map(roundCoords));

const roundGeomCoords = (g: DirectGeometryObject) => {
    switch (g.type) {
        case 'Point':
            return roundCoords(g.coordinates);
        case 'MultiPoint':
            return g.coordinates.map(roundCoords);
        case 'LineString':
            return g.coordinates.map(roundCoords);
        case 'MultiLineString':
            return roundPolygonCoords(g.coordinates);
        case 'Polygon':
            return roundPolygonCoords(g.coordinates);
        case 'MultiPolygon':
            return g.coordinates.map(c => roundPolygonCoords(c));
    }
};

// round the coordinates before stringify geometry. May ever need a geometry simplification?
const stringifyGeom = (geom: DirectGeometryObject) =>
    JSON.stringify({
        ...geom,
        coordinates: roundGeomCoords(geom),
    });

const encodeAsInputs = (attrs: Collection<string | string[]>) =>
    Object.keys(attrs)
        .map(name =>
            ensureArray(attrs[name]).map(value =>
                INPUT({
                    type: 'hidden',
                    name,
                    value,
                }),
            ),
        )
        .flat();

const exportLabel = (form: ExportForm) =>
    form === 'csv' ? tr.core('exportCSV') : tr.core('exportXLSX');

const getExportButton = (
    uniqueResourceIdentifier: string,
    geom: DirectGeometryObject,
    fields: string[],
    form: ExportForm,
) =>
    FORM(
        {
            method: 'post',
            action: checkURLScheme(uniqueResourceIdentifier),
        },
        ...encodeAsInputs({
            form,
            lang: getLang(),
            geom: stringifyGeom(geom),
            field: fields,
        }),
        INPUT({
            type: 'hidden',
            name: 'csrfmiddlewaretoken',
            value: getCSRF().getOrElse('__not_found__'),
        }),
        INPUT({
            className: 'btn-label',
            type: 'submit',
            value: exportLabel(form),
        }),
    );

const renderExportCSV = (loaded: HarvestedLayerLoaded) =>
    getDatasetMetadataOption(loaded.metadataId).chain(
        ({ uniqueResourceIdentifier }) =>
            getGeometry().map(geom =>
                DIV(
                    'btn btn-2 label-and-icon export-link__wrapper',
                    getExportButton(
                        uniqueResourceIdentifier,
                        geom,
                        getConfigLayerKeys(loaded),
                        'csv',
                    ),
                    SPAN('icon', nameToString('download')),
                ),
            ),
    );

const renderExportXLSX = (loaded: HarvestedLayerLoaded) =>
    getDatasetMetadataOption(loaded.metadataId).chain(
        ({ uniqueResourceIdentifier }) =>
            getGeometry().map(geom =>
                DIV(
                    'btn btn-2 label-and-icon export-link__wrapper',
                    getExportButton(
                        uniqueResourceIdentifier,
                        geom,
                        getConfigLayerKeys(loaded),
                        'xlsx',
                    ),
                    SPAN('icon', nameToString('download')),
                ),
            ),
    );

const getConfig = ({ mapId, metadataId, data }: HarvestedLayerLoaded) => {
    const layerInfo = getMapOption(mapId).chain(mi =>
        fromNullable(getMapLayers(mi).find(l => l.metadataId === metadataId)),
    );

    return layerInfo
        .chain(info => fromNullable(info.featureViewOptions))
        .map(options =>
            options.type === 'config'
                ? options
                : { ...options, keys: getLayerPropertiesKeys(data) },
        )
        .getOrElse({
            ...featureViewDefault,
            keys: getLayerPropertiesKeys(data),
        });
};

export const getConfigLayerKeys = (
    harvest: HarvestedLayerLoaded,
    withWidgets = false,
) => {
    const config = getConfig(harvest);
    if (config.type === 'default') {
        return getLayerPropertiesKeys(harvest.data);
    } else {
        const base = withWidgets
            ? config.rows
            : config.rows.filter(withoutWidget);

        const filteredRows = base.filter(r => r.lang === getLang());

        if (filteredRows.length > 0) {
            return filteredRows.map(row => row.propName);
        }

        return getLayerPropertiesKeys(harvest.data);
    }
};

// taken from compose :/
const WIDGET_PIECHART = '__WIDGET_PIECHART__';
const WIDGET_TEXT = '__WIDGET_TEXT__';
const WIDGETS = [WIDGET_PIECHART, WIDGET_TEXT];
const WIDGET_NAME: { [k: string]: MessageKey } = {
    [WIDGET_PIECHART]: 'featureViewTypePieChart',
    [WIDGET_TEXT]: 'featureViewTypeText',
};

const isWidget = (pn: string) =>
    WIDGETS.reduce((acc, w) => {
        if (acc) {
            return acc;
        }
        return w === pn;
    }, false);

const findColumnType = (data: FeatureCollection, key: string) =>
    getFields(data)
        .chain(findFirst<FieldsDescriptor>(([name]) => name === key))
        .map(([, fieldType]) => fieldType);

const columnValues = (features: Feature[], key: string): unknown[] =>
    compact(
        features.map(f =>
            fromNullable(f.properties).chain(props => fromNullable(props[key])),
        ),
    );

const renderNumberStatistics = (values: unknown[]) =>
    fromPredicate((ns: number[]) => ns.length > 0)(
        compact(values.map(tryNumber)),
    ).map(ns =>
        TH(
            'results__total',
            DIV(
                'results__total__elem',
                SPAN('label', concat(tr.view('report/sum'), ': ')),
                SPAN('value', formatNumber(ns.reduce((acc, n) => acc + n, 0))),
            ),
            DIV(
                'results__total__elem',
                SPAN('label', concat(tr.view('report/min'), ': ')),
                SPAN(
                    'value',
                    formatNumber(ns.reduce((acc, n) => Math.min(acc, n))),
                ),
            ),
            DIV(
                'results__total__elem',
                SPAN('label', concat(tr.view('report/max'), ': ')),
                SPAN(
                    'value',
                    formatNumber(ns.reduce((acc, n) => Math.max(acc, n))),
                ),
            ),
        ),
    );

const renderDateStatistics = (values: unknown[]) =>
    fromPredicate((ds: Date[]) => ds.length > 0)(
        compact(values.map(tryDate)),
    ).map(ds =>
        TH(
            'results__total',
            DIV(
                'results__total__elem',
                SPAN('label', concat(tr.view('report/min'), ': ')),
                SPAN(
                    'value',
                    formatDate(ds.reduce((acc, d) => (d < acc ? d : acc))),
                ),
            ),
            DIV(
                'results__total__elem',
                SPAN(
                    'label',
                    concat(tr.view('report/max'), ': '),
                    SPAN(
                        'value',
                        formatDate(ds.reduce((acc, d) => (d > acc ? d : acc))),
                    ),
                ),
            ),
        ),
    );

const renderStatisticsCell = ({ data }: HarvestedLayerLoaded, key: string) =>
    findColumnType(data, key)
        .chain(fieldType => {
            switch (fieldType) {
                case 'number':
                    return renderNumberStatistics(
                        columnValues(data.features, key),
                    );
                case 'date':
                case 'datetime':
                    return renderDateStatistics(
                        columnValues(data.features, key),
                    );
                default:
                    return none;
            }
        })
        .getOrElse(TH('results__total'));

const renderTable = (harvest: HarvestedLayerLoaded) => {
    const features = harvest.data.features;
    if (features.length === 0) {
        return DIV({});
    }
    const first = features[0];
    const props = first.properties;

    if (props === null) {
        return DIV({});
    }

    const config = getConfig(harvest);
    const keys = getConfigLayerKeys(harvest, true);

    const formatKey = (key: string) =>
        isWidget(key)
            ? tr.core(WIDGET_NAME[key])
            : getAlias(key, some(harvest.metadataId));

    const header = TR('', ...keys.map(key => TH('', formatKey(key))));

    const isStatisticsType = (fieldType: string) =>
        fieldType === 'number' ||
        fieldType === 'date' ||
        fieldType === 'datetime';

    const hasStatisticsColumn = (
        { data }: HarvestedLayerLoaded,
        keys: string[],
    ) =>
        keys.some(key =>
            findColumnType(data, key).fold(false, isStatisticsType),
        );

    const statistics = hasStatisticsColumn(harvest, keys)
        ? some(TR('', ...keys.map(key => renderStatisticsCell(harvest, key))))
        : none;

    const renderRow = (feature: Feature) =>
        renderConfig(config, feature, harvest.metadataId, true);

    return DETAILS(
        {
            className: 'result-table',
            open: true,
        },
        SUMMARY('', renderTitle(harvest.metadataId)),
        DIV(
            'table-actions',
            renderExportCSV(harvest),
            renderExportXLSX(harvest),
        ),
        TABLE(
            'results',
            THEAD('', header, statistics),
            TBODY({}, ...features.map(renderRow)),
        ),
    );
};

const renderHarvest = (harvest: HarvestedLayer) => {
    switch (harvest.tag) {
        case 'initial':
            return DIV({ key: harvest.metadataId }, 'initial');
        case 'loading':
            return DIV({ key: harvest.metadataId }, 'loading');
        case 'error':
            return DIV({ key: harvest.metadataId }, harvest.error);
        case 'loaded':
            return DIV({ key: harvest.metadataId }, renderTable(harvest));
    }
};

export const renderResults = () =>
    DIV(
        'results-wrapper',
        getHarvests()
            .map(h => h) // To override the readonly but we need to find a better solution
            .sort((a, b) => {
                if (a.groupId <= b.groupId && a.id < b.id) {
                    return -1;
                }
                return 1;
            })
            .map(renderHarvest),
    );
